import { tryParseJson } from './JsonTreeView';

/**
 * A judge run's verdicts, rendered as verdicts rather than as JSON.
 *
 * Both judges return a BATCH — one result per cell or per answer dimension —
 * so the raw payload is an array of near-identical objects, which is exactly
 * the shape a JSON tree renders worst. The thing a reader wants is the verdict
 * word, what it was about, and why; the itemId is plumbing.
 */

type Verdict = 'good' | 'acceptable' | 'bad';

interface QualityResult {
    itemId?: string;
    verdict?: string;
    conditionalVerdict?: string | null;
    accuracyAssessment?: string | null;
    approachAssessment?: string | null;
    failureTags?: string[];
    reasoning?: string;
}

interface EquivalenceResult {
    itemId?: string;
    score?: number;
    equivalent?: boolean;
    reasoning?: string;
}

const TONE: Record<string, string> = { good: 'ok', acceptable: 'warn', bad: 'bad' };

function VerdictBadge({ value, label }: { value?: string | null; label?: string }) {
    if (!value) return null;
    return (
        <span className={`jv-badge jv-${TONE[value] ?? 'neutral'}`}>
            {label ? `${label}: ${value}` : value}
        </span>
    );
}

/** Results out of a judge batch payload, whichever shape it is. */
function resultsOf(raw: unknown): Record<string, unknown>[] | null {
    if (!raw || typeof raw !== 'object') return null;
    const results = (raw as { results?: unknown }).results;
    return Array.isArray(results) ? results as Record<string, unknown>[] : null;
}

export function asJudgeBatch(value: unknown): Record<string, unknown>[] | null {
    return resultsOf(value);
}

/** Parse a raw judge response into its results array, or null. */
export function judgeBatchFromRaw(raw: string | null | undefined): Record<string, unknown>[] | null {
    if (!raw) return null;
    const parsed = tryParseJson(raw);
    return parsed.ok ? resultsOf(parsed.value) : null;
}

export function AssessmentQualityVerdictsView({ results }: { results: Record<string, unknown>[] }) {
    const items = results as unknown as QualityResult[];
    return (
        <div className="jv">
            <div className="jv-summary">
                {(['good', 'acceptable', 'bad'] as Verdict[]).map(v => {
                    const n = items.filter(i => i.verdict === v).length;
                    return n > 0 ? <VerdictBadge key={v} value={v} label={String(n)} /> : null;
                })}
                <span className="jv-count">{items.length} cell{items.length === 1 ? '' : 's'} judged</span>
            </div>

            {items.map((item, i) => (
                <div key={item.itemId ?? i} className="jv-item">
                    <div className="jv-verdicts">
                        <VerdictBadge value={item.verdict} />
                        {/* Only when it DIFFERS: "conditional: good" beside "good"
                            is noise, while a gap between them is the finding —
                            the step reasoned well from a bad transcription. */}
                        {item.conditionalVerdict && item.conditionalVerdict !== item.verdict && (
                            <VerdictBadge value={item.conditionalVerdict} label="given transcription" />
                        )}
                        <VerdictBadge value={item.accuracyAssessment} label="accuracy" />
                        <VerdictBadge value={item.approachAssessment} label="approach" />
                        {(item.failureTags ?? []).map(tag => (
                            <span key={tag} className="jv-tag">{tag}</span>
                        ))}
                    </div>
                    {item.reasoning && <p className="jv-reason">{item.reasoning}</p>}
                    {/* The cell this verdict is about — `${rowId}::${outputId}`.
                        Plumbing, but it is the only way back to the run. */}
                    {item.itemId && <code className="jv-item-id">{item.itemId}</code>}
                </div>
            ))}
        </div>
    );
}

export function EquivalenceVerdictsView({ results }: { results: Record<string, unknown>[] }) {
    const items = results as unknown as EquivalenceResult[];
    const agreed = items.filter(i => i.equivalent).length;
    return (
        <div className="jv">
            <div className="jv-summary">
                <span className="jv-badge jv-ok">{agreed} equivalent</span>
                <span className="jv-badge jv-bad">{items.length - agreed} not</span>
                <span className="jv-count">{items.length} answer{items.length === 1 ? '' : 's'} judged</span>
            </div>

            {items.map((item, i) => (
                <div key={item.itemId ?? i} className="jv-item">
                    <div className="jv-verdicts">
                        <span className={`jv-badge ${item.equivalent ? 'jv-ok' : 'jv-bad'}`}>
                            {item.equivalent ? 'equivalent' : 'not equivalent'}
                        </span>
                        {item.score !== undefined && <span className="jv-tag">score {item.score}</span>}
                    </div>
                    {item.reasoning && <p className="jv-reason">{item.reasoning}</p>}
                    {item.itemId && <code className="jv-item-id">{item.itemId}</code>}
                </div>
            ))}
        </div>
    );
}
